import type { Action } from "../engine.js";
import { normalizeText } from "../rules.js";
import { addDays, atGameDay, dayKey, isDayKey, localMinutes, parseHm } from "../time.js";
import {
  ACTIVITY_ANIMATIONS,
  AREAS,
  COMPANION_ACTIVITIES,
  MODES,
  MONEY_TIERS,
  MUTE_KINDS,
  SIZES,
  type ActivityAnimation,
  type Area,
  type GameState,
  type Size,
} from "../types.js";
import { parseAt, parsePolishDate } from "./dates.js";

export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

const str = (v: unknown, max = 200) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const oneOf = <T extends string>(v: unknown, list: readonly T[]): T | null =>
  typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : null;

/** Resolve a reminder time from the model ("19:00", "wieczorem", ISO, empty) into an epoch. */
export function resolveReminderAt(at: string, state: GameState, now: number): number {
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const nowMin = localMinutes(now, tz);
  const p = parseAt(at);
  let minutes = p.minutes;
  if (minutes === null) {
    if (p.part === "morning") minutes = 8 * 60;
    else if (p.part === "afternoon") minutes = 15 * 60;
    else if (p.part === "evening") minutes = parseHm(state.settings.eveningAt) ?? 19 * 60;
    // No time given: the nearest sensible window, i.e. the evening debrief.
    else minutes = parseHm(state.settings.debriefFrom) ?? 21 * 60;
  }
  let day = p.date ?? parsePolishDate(at, today) ?? today;
  // Late night (before 4:00) belongs to the game day; compare in game-day minutes.
  const gm = (m: number) => (m < 240 ? m + 1440 : m);
  if (!p.date && day === today && gm(minutes) <= gm(nowMin)) day = addDays(today, 1);
  return atGameDay(day, minutes, tz);
}

function findQuestId(state: GameState, raw: string): string | null {
  const open = state.quests.filter((q) => q.status === "open");
  const exact = open.find((q) => q.id === raw);
  if (exact) return exact.id;
  const n = normalizeText(raw);
  return open.find((q) => normalizeText(q.title) === n)?.id ?? null;
}

/** Map one tool call to a game action. Invalid input yields an error the model sees as the tool result. */
export function toolCallToAction(
  call: ToolCall,
  state: GameState,
  now: number,
): { action: Action } | { error: string } {
  const i = call.input ?? {};
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  switch (call.name) {
    case "log_activity": {
      const area = oneOf<Area>(i.area, AREAS);
      if (!area) return { error: "area must be body|mind|work|home" };
      const size = oneOf<Size>(i.size, SIZES) ?? "S";
      const animation = oneOf<ActivityAnimation>(i.animation, ACTIVITY_ANIMATIONS) ?? undefined;
      return {
        action: { type: "log_activity", area, text: str(i.text, 120) || "aktywność", size, animation },
      };
    }
    case "add_quest": {
      const title = str(i.title, 80);
      if (!title) return { error: "title required" };
      const area = oneOf<Area>(i.area, AREAS) ?? "home";
      const size = oneOf<Size>(i.size, SIZES) ?? "S";
      const dueRaw = str(i.due, 40);
      const due =
        dueRaw === "today" || dueRaw === "tomorrow" || isDayKey(dueRaw)
          ? dueRaw
          : (parsePolishDate(dueRaw, today) ?? "today");
      const rep = Number(i.repeat_days);
      return { action: { type: "add_quest", title, area, size, due, repeatDays: rep > 0 ? rep : null } };
    }
    case "complete_quest":
    case "postpone_quest":
    case "drop_quest": {
      const id = findQuestId(state, str(i.id, 80));
      if (!id) return { error: "unknown quest id" };
      if (call.name === "complete_quest") return { action: { type: "complete_quest", id } };
      if (call.name === "drop_quest") return { action: { type: "drop_quest", id } };
      const to = str(i.to, 20);
      return { action: { type: "postpone_quest", id, to: isDayKey(to) ? to : parsePolishDate(to, today) } };
    }
    case "add_reminder": {
      const text = str(i.text, 100);
      if (!text) return { error: "text required" };
      return { action: { type: "add_reminder", text, at: resolveReminderAt(str(i.at, 40), state, now) } };
    }
    case "set_mode": {
      const mode = oneOf(i.mode, MODES);
      if (!mode) return { error: "mode must be normal|sick|vacation|crunch" };
      const until = str(i.until, 20);
      return { action: { type: "set_mode", mode, until: isDayKey(until) ? until : null } };
    }
    case "mute": {
      const kind = oneOf(i.kind, MUTE_KINDS);
      if (!kind) return { error: "kind must be short|today|days" };
      const until = str(i.until, 20);
      return {
        action: { type: "mute", kind, untilDay: isDayKey(until) ? until : parsePolishDate(until, today) },
      };
    }
    case "unmute":
      return { action: { type: "unmute" } };
    case "money_event": {
      const tier = oneOf(i.tier, MONEY_TIERS);
      if (!tier) return { error: "tier must be small|medium|large" };
      return { action: { type: "money_event", tier } };
    }
    case "buy_item":
      return { action: { type: "buy_item", itemId: str(i.item_id, 40) } };
    case "remember_fact": {
      const fact = str(i.fact, 140);
      return fact ? { action: { type: "remember_fact", text: fact } } : { error: "fact required" };
    }
    case "forget_fact": {
      const id = str(i.id, 40);
      const f =
        state.facts.find((x) => x.id === id) ??
        state.facts.find((x) => normalizeText(x.text).includes(normalizeText(id)));
      return f ? { action: { type: "forget_fact", id: f.id } } : { error: "unknown fact id" };
    }
    case "shopping_add":
    case "shopping_remove": {
      const items = Array.isArray(i.items)
        ? i.items
            .map((x) => str(x, 40))
            .filter(Boolean)
            .slice(0, 20)
        : [];
      if (!items.length) return { error: "items required" };
      return { action: { type: call.name, items } };
    }
    case "shopping_clear":
      return { action: { type: "shopping_clear" } };
    case "companion_do": {
      const activity = oneOf(i.activity, COMPANION_ACTIVITIES);
      if (!activity) return { error: `activity must be one of ${COMPANION_ACTIVITIES.join("|")}` };
      const minutes = i.minutes === undefined || i.minutes === null ? null : Math.round(Number(i.minutes));
      if (minutes !== null && !(minutes >= 1 && minutes <= 720)) return { error: "minutes must be 1-720" };
      return { action: { type: "companion_do", activity, minutes } };
    }
    case "rate_week": {
      const v = Math.round(Number(i.value));
      return v >= 1 && v <= 5 ? { action: { type: "rate_week", value: v } } : { error: "value must be 1-5" };
    }
    default:
      return { error: `unknown tool ${call.name}` };
  }
}
